<template>
  <section>
    <h2>計算</h2>
    <div v-for="(entry,i) in history2" :key="i" class="container">
      <div class="math">
	<span v-if="i>0" class="equal"> → </span>
	<ShowMatrix :matrix="entry.matrix" :config="config"></Showmatrix>
      </div>
      <ul class="history">
	<li v-for="op of entry.ops" :key="op.id">
	  {{ op.op.txt(false,config.show2=='eqn') }}
	  <button @click="cancel(op.id)"> 取消 </button>
	</li>
      </ul>
    </div>
    <div class="container" ref="main">
      <div class="math">
	<span v-if="history.length >0" class="equal"> → </span>
	<ShowMatrix :matrix="root" :op="action" :config="config" :moving="moving" :delay="delay" :hint="hint" @finish="next"></ShowMatrix>
      </div>
      <p v-if="root.reduced">
	簡約
      </p>
      <div v-if="calc == 'reduction' || history.length == 0 || ! root.reduced">
	<ul>
	  <li :class="{selected: action.type=='mult'}">
	    <label for="mult">[s]<input type="radio" v-model="action.type" value="mult" id="mult"/>
	      <InputNum :op="action" prop="mult_i" :size="rows" :eqn="eqn" ref="key_s"></InputNum> を
	      <InputCoeff :op="action" prop="coeff_mult"></InputCoeff>倍する。
	      <button v-if="action.type == 'mult' && message == ''" class="exec" @click="exec"> 実行 </button>
	    </label>
	  </li>
	  <li :class="{selected: action.type=='swap'}">
	    <label for="swap">[d]<input type="radio" v-model="action.type" value="swap" id="swap"/>
	      <InputNum :op="action" prop="swap_i1" :size="rows" :eqn="eqn" ref="key_d"></InputNum> と
	      <InputNum :op="action" prop="swap_i2" :size="rows" :eqn="eqn"></InputNum> を入れ替える。
	      <button v-if="action.type == 'swap' && message == ''" class="exec" @click="exec"> 実行 </button>
	    </label>
	  </li>
	  <li :class="{selected: action.type=='add'}">
	    <label for="add">[f]<input type="radio" v-model="action.type" value="add" id="add"/>
	      <InputNum :op="action" prop="add_i1" :size="rows" :eqn="eqn" ref="key_f"></InputNum> の
	      <input type="text" :class="{error: action.coeff_add.error}"  v-model="action.coeff_add.input"
		     @focus="action.type = 'add'"/> 倍を
	      <InputNum :op="action" prop="add_i2" :size="rows" :eqn="eqn"></InputNum> に加える。
	      <button v-if="action.type == 'add' && message == ''" class="exec" @click="exec"> 実行 </button>
	    </label>
	  </li>
	</ul>
	<p v-if="hint && ! root.reduced">
	  ヒント : {{root.hint.txt}}
	  <button @click="useHint">次の変形</button>
	</p>
	<p>
	  <span class="warning">{{message}}</span>
	</p>
      </div>
    </div>
    <p>
      <ul>
	<li v-if="calc == 'eqn'">
	  表示形式
	  <ul>
	    <li>
	      <label for="eqn2"><input type="radio" v-model="config.show2" value="eqn" id="eqn2" /> 連立方程式 </label>
	    </li>
	    <li>
	      <label for="aug2"><input type="radio" v-model="config.show2" value="aug" id="aug2" /> 拡大係数行列 </label>
	    </li>
	  </ul>
	</li>
	<template v-if="! root.reduced">
	  <li>
	    <label for="checkboxH"><input type="checkbox" id="checkboxH" v-model="hint">
		ヒントを表示</label>
	  </li>
	  <li>
	    <label for="checkboxA"><input type="checkbox" id="checkboxA" v-model="animation">
		変形のアニメーションを表示</label>
	    <span v-if="animation" class="bar">
	      速←<input type="range" min="300" max="3000" v-model.number="delay" />→遅
	    </span>
	  </li>
	</template>
      </ul>
    </p>
    <template v-if="root.reduced && calc=='eqn'">
      <h3> 解 </h3>      
      <Solution :matrix="root" :config="config"></Solution>
    </template>
    <template v-if="root.reduced && calc=='inverse'">
      <h3> 結果 </h3>
      <div v-if="inverse != null" class="math">
	<span class="equal">逆行列は</span>
	<ShowMatrix :matrix="inverse" :op="null" :config="config"></ShowMatrix>
      </div>
      <p v-else>
	逆行列はない。
      </p>
    </template>
    <div v-if="tex">
      <span v-for="(line,i) of texSource" :key="i">
	{{line}} <br />
      </span>
    </div>
  </section>
</template>

<script>
import { Operation, Config, one, zero } from '@/lib/base.js';
import { Matrix } from '@/lib/reduce.js';
import ShowMatrix from '@/components/ShowMatrix.vue'
import InputNum from '@/components/InputNum.vue'
import InputCoeff from '@/components/InputCoeff.vue'
import Solution from '@/components/Solution.vue'

export default {
  name: 'Reduction',
  components: {
    ShowMatrix,
    InputNum,
    InputCoeff,
    Solution
  },
  props: {
    calc: String,
    original: Array,
    config: Config,
  },
  data: function(){
    return {
      action: new Operation(),
      root: null,
      history: [],
      tex: false,
      animation: true,
      hint: false,
      hIndex: 0,
      moving: false,
      delay: 500,
    }
  },
  created: function(){
    const rows = this.original.length;
    const cols = (this.calc != 'inverse') ? this.original[0].length : rows*2;
    const aug = (this.calc == 'eqn') ? cols-1 : (this.calc == 'inverse') ? rows : -1;
    this.root = new Matrix(rows,cols,aug);
    for(let i=0; i<this.rows; i++){
      const r = [];
      for(const e of this.original[i]){
	r.push(e.frac.clone());
      }
      if(this.calc == 'inverse'){
	for(let j=0; j<rows; j++){
	  r.push( i == j ? one : zero);
	}
      }
      this.root.matrix.push(r);	
    }    
  },
  mounted: function(){
    this.$refs.main.scrollIntoView({behavior: "smooth", block: "center", inline: "nearest"});
  },
  methods: {
    exec(){
      if(this.animation){
	this.moving = true;
      }else{
	this.next();
      }
    },
    next(){
      this.moving = false;
      this.history.push({
	matrix: this.root,
	op: this.action
      });
      this.root = this.root.apply(this.action);
      this.action = new Operation();
      this.hIndex = 0;
      this.$nextTick( () => {this.$refs.main.scrollIntoView({behavior: "smooth", block: "center", inline: "nearest"});});
    },
    cancel(i){
      this.root = this.history[i].matrix;
      this.action = this.history[i].op;
      this.history.splice(i);
    },
    keyUp(event){
      if('sdfz'.includes(event.key) && this.$refs['key_' + event.key]){
	if(this.$refs['key_' + event.key].$refs){
	  this.$refs['key_' + event.key].$refs.window.focus();
	  if('sdf'.includes(event.key)){
	    this.$refs['key_' + event.key].$refs.window.setSelectionRange(0,10);	    
	  }
	}else{
	  this.$refs['key_' + event.key].focus();
	}
      }else if(event.key == '.'){
	this.tex = ! this.tex;
      }
    },
    invalidRow(i){
      return ! Number.isInteger(i) || i < 0 || i >= this.rows ;
    },
    useHint(){
      this.action = this.root.hint.actions[this.hIndex];
      this.hIndex +=1;
      if(this.hIndex >= this.root.hint.actions.length){
	this.hIndex = 0;
      }
    }
  },
  computed: {
    eqn(){
      return (this.config.show2 == 'eqn');
    },
    rows(){
      return this.original.length;
    },
    inverse(){
      if(! this.root.reduced || this.calc != 'inverse' || ! this.root.matrix[this.rows-1][this.rows-1].equals(one) ){
	return null;
      }
      const result = new Matrix(this.rows,this.rows,-1);
      for(let i=0;i<this.rows;i++){
	result.matrix.push(this.root.matrix[i].slice(this.rows));
      }
      return result;
    },
    history2(){
      const ar = [];
      for(let i=0; i<this.history.length; i++){
	const entry = this.history[i];
	if(i>0 && entry.op.type == 'add' && this.history[i-1].op.type == 'add' && entry.op.add_i1 == this.history[i-1].op.add_i1){
	  ar[ar.length-1].ops.push({ op: entry.op, id: i });
	}else{
	  ar.push({
	    matrix: entry.matrix,
	    ops: [ { op: entry.op, id: i } ]
	  })
	}
      }
      return ar;
    },
    message(){
      if(! ['mult','swap','add'].includes(this.action.type)){
	return '変形の種類を指定してください。';
      }else if(this.action.type == 'mult' && this.action.coeff_mult.error || this.action.type == 'add' && this.action.coeff_add.error){
	return 'スカラー(有理数)の入力に誤りがあります。';
      }else if(this.action.type == 'mult' && this.action.coeff.equals(zero)){
	return '0をかけることはできません。';
      }else if(this.action.type == 'mult' && this.invalidRow(this.action.mult_i)
	       || this.action.type == 'swap' && (this.invalidRow(this.action.swap_i1) || this.invalidRow(this.action.swap_i2))
	       || this.action.type == 'add' && (this.invalidRow(this.action.add_i1) || this.invalidRow(this.action.add_i2)) ){
	return this.action.rcTxt + '番号に誤りがあります。';	
      }else if(this.action.type == 'swap' && this.action.swap_i1 == this.action.swap_i2){
	return this.action.rcTxt + '番号が同一です。';
      }else if(this.action.type == 'add' && this.action.add_i1 == this.action.add_i2){
	return this.action.rcTxt + '番号が同一です。';
      }else if(this.action.type == 'add' && this.action.coeff.equals(zero) || this.action.type == 'mult' && this.action.coeff.equals(one)){
	return 'この操作で' + (this.eqn ? '連立方程式' : '行列') + 'は変化しません。';
      }else if(this.moving){
	return '計算中';
      }else{
	return '';
      }
    },
    texSource(){
      const begin =  (this.root.aug > 0) ? '\\begin{amatrix}{' + this.root.aug + '}{' + (this.root.cols - this.root.aug) + '}' : '\\mat{';
      const end = (this.root.aug > 0) ? '\\end{amatrix}' : '}';
      const ar = [];
      ar.push('\\begin{minipage}{200pt}');
      ar.push(' \\small');
      ar.push(' \\begin{align*}');
      ar.push(' \\elemrow{' + this.rows + '}');
      ar.push('[A|\\vb] &=');
      for(let n=0; n<this.history2.length; n++){
	const entry = this.history2[n];
	ar.push(begin);
	for(let i=0; i<entry.matrix.matrix.length; i++){
	  const row = entry.matrix.matrix[i];
	  const ar2 = [];
	  for(const e of row){
	    ar2.push(e.toFraction());
	  }
	  let txt = ar2.join(' & ');
	  if(i < this.rows - 1){
	    txt += ' \\\\';
	  }
	  ar.push(txt);
	}
	ar.push(end);
	for(const obj of entry.ops){
	  const op = obj.op;
	  if(op.type == 'mult'){
	    if(op.coeff_mult.frac.s < 0){
	      ar.push('\\elemmul[y]{' + (op.mult_i +1) + '}{' + op.coeff_mult.frac.toFraction() + '}');
	    }else{
	      ar.push('\\elemmul{' + (op.mult_i +1) + '}{' + op.coeff_mult.frac.toFraction() + '}');
	    }
	  }else if(op.type == 'swap'){
	    ar.push('\\elemexch{' + (op.swap_i1+1) + '}{' + (op.swap_i2+1) + '}');
	  }else if(op.type == 'add'){
	    if(op.coeff_add.frac.s < 0){
	      ar.push('\\elemaddw[y]{' + (op.add_i1+1) + '}{' + op.coeff_add.frac.toFraction() + '}{' + (op.add_i2+1) + '}');
	    }else{
	      ar.push('\\elemaddw[n]{' + (op.add_i1+1) + '}{' + op.coeff_add.frac.toFraction() + '}{' + (op.add_i2+1) + '}');
	    }
	  }
	}
	if( n == Math.floor(this.history2.length /2) ){
	  ar.push('\\end{align*}');
	  ar.push('\\end{minipage}');
	  ar.push('\\begin{minipage}{200pt}');
	  ar.push(' \\small');
	  ar.push(' \\begin{align*}');
	  ar.push(' \\elemrow{' + this.rows + '}');
	  ar.push(' \\phantom{A} &\\to');
	}else{
	  ar.push('\\\\');
	  ar.push('&\\to');
	}
      }
      ar.push(begin);
      for(let i=0; i< this.root.matrix.length; i++){
	const row = this.root.matrix[i];
	const ar2 = [];
	for(const e of row){
	  ar2.push(e.toFraction());
	}
	let txt = ar2.join(' & ');
	if(i < this.rows - 1){
	  txt += ' \\\\';
	}
	  ar.push(txt);
      }
      ar.push(end);
      ar.push('\\end{align*}');
      ar.push('\\end{minipage}');
      return ar;
    }
  }
}
</script>

<style scoped>
.bar{
    display: flex;
    align-items: center;    
}
</style>
